<template>
  <div>
    <!-- Read-only display for non-Payer users -->
    <span v-if="!canEditDate" class="tw-text-sm">
      {{ formatDate(localOrder.service_date) }}
    </span>

    <!-- Editable date picker for Payer users -->
    <div v-else class="tw-flex tw-items-center tw-gap-2">
      <Popover v-model:open="isOpen">
        <PopoverTrigger as-child>
          <Button
            variant="outline"
            size="sm"
            class="tw-h-8 tw-w-auto tw-justify-start tw-text-left tw-font-normal"
            :class="{ 'tw-text-muted-foreground': !localOrder.service_date }"
          >
            <div class="tw-flex tw-flex-col tw-items-start">
              <!-- Original service date (if changed) -->
              <span
                v-if="localOrder.new_service_date && localOrder.service_date"
                class="tw-text-xs tw-text-red-500 tw-line-through"
              >
                {{ formatDate(localOrder.service_date) }}
              </span>

              <!-- Current/new service date -->
              <span class="tw-text-xs">
                {{ formatDate(localOrder.new_service_date || localOrder.service_date) }}
              </span>
            </div>
            <CalendarIcon class="tw-ml-2 tw-h-3 tw-w-3" />
          </Button>
        </PopoverTrigger>

        <PopoverContent class="tw-w-auto tw-p-0" align="start">
          <Calendar
            v-model="selectedDate"
            @update:model-value="onDateSelect"
            :default-value="selectedDate"
            mode="single"
            initial-focus
          />
        </PopoverContent>
      </Popover>

      <!-- Save button (only show if date has been changed) -->
      <Button
        v-if="localOrder.new_service_date"
        size="sm"
        variant="ghost"
        class="tw-h-8 tw-px-2"
        @click="saveServiceDate"
        :disabled="isSaving"
      >
        <SaveIcon v-if="!isSaving" class="tw-h-4 tw-w-4 !tw-text-primary-600" />
        <LoaderIcon v-else class="tw-h-4 tw-w-4 tw-animate-spin" />
      </Button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { format } from 'date-fns';
import { parseDate } from '@internationalized/date';
import type { DateValue } from '@internationalized/date';
import { Calendar as CalendarIcon, Save as SaveIcon, Loader as LoaderIcon } from 'lucide-vue-next';
import { Calendar } from '@/components/ui/calendar';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useUserStore } from '@/stores/user.store';
import { useOrderStore } from '@/stores/order.store';
import { useToast } from 'vue-toast-notification';
import { storeToRefs } from 'pinia';

interface Props {
  order: {
    id: number;
    service_date: string | null;
    new_service_date?: string | null;
  };
  onOrderUpdate?: (orderId: number, updatedOrder: any) => void;
}

const props = defineProps<Props>();
const emit = defineEmits<{
  (e: 'order-updated', orderId: number, updatedOrder: any): void;
}>();

const { currentUser } = storeToRefs(useUserStore());
const { updateOrderServiceDate } = useOrderStore();
const toast = useToast();

const isOpen = ref(false);
const isSaving = ref(false);
const selectedDate = ref<DateValue | undefined>();
const localOrder = ref({ ...props.order });

// Check if user has Payer role
const canEditDate = computed(() => {
  return currentUser.value?.user_role?.includes('Payer') || false;
});

// Format date for display
const formatDate = (dateString: string | null) => {
  if (!dateString) return '-';
  try {
    return format(new Date(dateString), 'dd.MM.yyyy');
  } catch {
    return '-';
  }
};

// Convert string date to DateValue
const stringToDateValue = (dateString: string | null): DateValue | undefined => {
  if (!dateString) return undefined;
  try {
    return parseDate(dateString);
  } catch {
    return undefined;
  }
};

// Convert DateValue to string
const dateValueToString = (date: DateValue): string => {
  return `${date.year}-${String(date.month).padStart(2, '0')}-${String(date.day).padStart(2, '0')}`;
};

// Initialize selected date
const initializeSelectedDate = () => {
  const dateToUse = localOrder.value.new_service_date || localOrder.value.service_date;
  selectedDate.value = stringToDateValue(dateToUse);
};

// Handle date selection
const onDateSelect = (date: DateValue | undefined) => {
  if (!date) return;

  const newDateString = dateValueToString(date);
  const originalDateString = localOrder.value.service_date;

  // If the new date is the same as original, clear the new_service_date
  if (newDateString === originalDateString) {
    localOrder.value.new_service_date = null;
  } else {
    localOrder.value.new_service_date = newDateString;
  }

  isOpen.value = false;
};

// Save the service date
const saveServiceDate = async () => {
  if (!localOrder.value.new_service_date) return;

  isSaving.value = true;

  try {
    await updateOrderServiceDate(localOrder.value.id, {
      service_date: localOrder.value.new_service_date,
    });

    // Update the local order object
    const updatedOrder = {
      ...localOrder.value,
      service_date: localOrder.value.new_service_date,
      new_service_date: null,
    };

    localOrder.value = updatedOrder;

    // Emit the update event
    emit('order-updated', localOrder.value.id, updatedOrder);

    // Show success message
    toast.open({
      message: 'Service date has been updated.',
      type: 'success',
    });

    // Reinitialize the selected date
    initializeSelectedDate();
  } catch (error: any) {
    console.error('Error updating service date:', error);

    // Show error message
    const errorMessage =
      error?.response?.data?.errors?.[0]?.detail ||
      error?.response?.data?.message ||
      'Failed to update service date.';

    toast.open({
      message: errorMessage,
      type: 'error',
    });
  } finally {
    isSaving.value = false;
  }
};

// Watch for changes in order prop to reinitialize local data
watch(
  () => props.order,
  newOrder => {
    localOrder.value = { ...newOrder };
    initializeSelectedDate();
  },
  { immediate: true, deep: true }
);
</script>
